<template>
  <div class="guide-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <!-- ===== Hero ===== -->
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Reportes / Guía</p>
            <h1 class="list-title">Cómo leer los reportes</h1>
            <p class="list-subtitle">
              Qué pregunta responde cada reporte, cómo se interpretan sus
              números y qué advertencias tiene el dato antes de tomar una
              decisión con él.
            </p>
          </div>
        </v-flex>

        <!-- ===== Columna principal ===== -->
        <v-flex md8 xs12>
          <!-- Un bloque por reporte, generado desde la lista para que agregar
               un reporte nuevo sea una entrada más y no otro bloque copiado. -->
          <v-card
            v-for="(rep, index) in reportes"
            :key="rep.route"
            class="guide-card"
          >
            <div class="guide-step">
              <span class="guide-step__num">{{ index + 1 }}</span>
              <div class="guide-step__body">
                <h2 class="guide-step__title">{{ rep.titulo }}</h2>
                <p class="guide-step__text">
                  <strong>Responde:</strong> {{ rep.pregunta }}
                </p>

                <p class="guide-label">Qué muestran las tarjetas</p>
                <ul class="guide-fields">
                  <li v-for="t in rep.tarjetas" :key="t.nombre">
                    <strong>{{ t.nombre }}</strong> — {{ t.significado }}
                  </li>
                </ul>

                <p class="guide-label">Filtros</p>
                <p class="guide-step__text">{{ rep.filtros }}</p>

                <!-- La advertencia es lo que evita que alguien reporte un
                     número equivocado hacia arriba. -->
                <div v-if="rep.aviso" class="guide-warn">
                  <v-icon small class="guide-warn__icon">warning</v-icon>
                  <span><strong>Ojo:</strong> {{ rep.aviso }}</span>
                </div>

                <router-link :to="rep.route" class="guide-cta">
                  <v-icon small class="guide-cta__icon">open_in_new</v-icon>
                  Abrir {{ rep.corto }}
                </router-link>
              </div>
            </div>
          </v-card>

          <!-- ===== Estado del dato ===== -->
          <v-card class="guide-card">
            <h2 class="guide-section__title">
              <v-icon small class="guide-aside__icon">fact_check</v-icon>
              Estado de los datos
            </h2>
            <p class="guide-step__text">
              Los reportes son tan buenos como lo que se captura. Esto es lo que
              hoy está sano y lo que no:
            </p>
            <table class="rpt-table" style="margin-top: 12px">
              <thead>
                <tr>
                  <th>Dato</th>
                  <th>Estado</th>
                  <th>Qué implica</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in estadoDatos" :key="d.campo">
                  <td class="rpt-strong">{{ d.campo }}</td>
                  <td>
                    <span
                      :class="[
                        'rpt-pill',
                        d.ok ? 'rpt-pill--ok' : 'rpt-pill--warn',
                      ]"
                    >
                      {{ d.ok ? 'Sano' : 'Revisar' }}
                    </span>
                  </td>
                  <td class="rpt-muted">{{ d.implica }}</td>
                </tr>
              </tbody>
            </table>
          </v-card>
        </v-flex>

        <!-- ===== Panel lateral ===== -->
        <v-flex md4 xs12>
          <v-card class="guide-card">
            <h3 class="guide-aside__title">
              <v-icon small class="guide-aside__icon">file_download</v-icon>
              Exportar a Excel
            </h3>
            <ul class="guide-aside__list">
              <li>
                El botón <strong>Exportar</strong> genera un archivo con varias
                hojas: detalle, agrupación y tendencia.
              </li>
              <li>
                Exporta <strong>lo que estás viendo</strong>, ya filtrado. El
                archivo y la pantalla nunca se contradicen.
              </li>
              <li>
                Si ves el aviso <em>“Se muestran las N más…”</em>, el Excel trae
                sólo esas N. Sube <strong>Filas de detalle</strong> antes de
                exportar.
              </li>
              <li>El máximo por exportación son 500 filas de detalle.</li>
            </ul>
          </v-card>

          <v-card class="guide-card">
            <h3 class="guide-aside__title">
              <v-icon small class="guide-aside__icon">tune</v-icon>
              Cómo funcionan los filtros
            </h3>
            <ul class="guide-aside__list">
              <li>
                Cambiar cualquier filtro <strong>recarga solo</strong>; no hace
                falta presionar Actualizar.
              </li>
              <li>
                <strong>Desde / Hasta</strong> aplican sobre la fecha propia de
                cada reporte: recepción en Clientes, rechazo en Rechazos.
              </li>
              <li>
                Si su usuario es de laboratorio o distribuidora, sólo verá sus
                propios datos, aunque no aplique ningún filtro.
              </li>
            </ul>
          </v-card>

          <v-card class="guide-card">
            <h3 class="guide-aside__title">
              <v-icon small class="guide-aside__icon">schedule</v-icon>
              Cómo se miden los días
            </h3>
            <ul class="guide-aside__list">
              <li>
                Siempre <strong>días calendario</strong>, no hábiles: es lo que
                cuenta el cliente.
              </li>
              <li>
                El <strong>ciclo</strong> se mide de recepción a salida de
                Farmacia y Drogas, no hasta la entrega.
              </li>
              <li>
                Para tiempos de trámite y motivos de pendiente, use el
                <strong>KPI del Dashboard</strong>: estos reportes no lo
                duplican.
              </li>
            </ul>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
// El contenido vive en datos, no en el template: así la guía se actualiza
// editando una entrada y no reescribiendo bloques de HTML.
const REPORTES = [
  {
    corto: 'Cartera Pendiente',
    titulo: 'Cartera pendiente de liquidación',
    route: '/reportes/cartera-pendiente',
    pregunta: '¿qué facturas siguen sin liquidar y cuánto dinero representan?',
    filtros: 'Laboratorio, Distribuidora y cantidad de filas de detalle.',
    tarjetas: [
      {
        nombre: 'Monto pendiente (CIF)',
        significado: 'suma del valor de todo lo no liquidado.',
      },
      {
        nombre: 'Antigüedad promedio',
        significado: 'días transcurridos desde que se recibió la factura.',
      },
      {
        nombre: 'Más de 60 días',
        significado:
          'cuántas facturas y qué porcentaje están en el tramo viejo.',
      },
      {
        nombre: 'Aún sin salir de F&D',
        significado: 'las que siguen realmente en trámite.',
      },
    ],
    aviso:
      'una antigüedad promedio de cientos de días no es deuda real: son facturas ya resueltas que nunca se marcaron como liquidadas. Depure el histórico antes de usar este monto como indicador financiero. El reporte le avisa automáticamente cuando esto ocurre.',
  },
  {
    corto: 'Vencimientos',
    titulo: 'Vencimientos y certificación de lotes',
    route: '/reportes/vencimientos',
    pregunta:
      '¿qué lotes vencieron o están por vencer, y cuáles no tienen certificado?',
    filtros:
      'Laboratorio, horizonte de alerta (30 a 365 días) y filas de detalle. El horizonte define qué se considera “por vencer”.',
    tarjetas: [
      {
        nombre: 'Lotes vencidos',
        significado: 'ya pasaron su fecha de vencimiento (tarjeta roja).',
      },
      {
        nombre: 'Vencen en N días',
        significado: 'ventana para actuar de forma preventiva.',
      },
      {
        nombre: 'Sin certificado',
        significado: 'lotes con la certificación pendiente.',
      },
      {
        nombre: 'Registro sanitario vencido',
        significado: 'el producto perdió vigencia de registro.',
      },
    ],
    aviso:
      'el vencimiento se calcula por la FECHA del lote, no por su estado guardado. Hay miles de lotes vencidos que el sistema no tiene marcados como vencidos, así que el campo “estado” no es confiable hasta que se corrija el proceso que lo actualiza.',
  },
  {
    corto: 'Volumen por Cliente',
    titulo: 'Volumen y valor por cliente',
    route: '/reportes/clientes',
    pregunta:
      '¿quién trae volumen, quién trae valor y a quién cuesta más procesarle?',
    filtros:
      'Desde, Hasta, Laboratorio y Distribuidora. Por defecto, los últimos 12 meses.',
    tarjetas: [
      {
        nombre: 'Valor facturado (CIF)',
        significado: 'total del periodo seleccionado.',
      },
      {
        nombre: 'Ciclo promedio',
        significado: 'días de recepción a salida de Farmacia y Drogas.',
      },
      {
        nombre: 'Tasa de rechazo',
        significado: 'porcentaje de facturas denegadas.',
      },
      {
        nombre: 'Pendientes de liquidar',
        significado: 'las generadas dentro del periodo.',
      },
    ],
    aviso:
      'en la tabla, el color de las píldoras marca umbrales: ciclo en rojo sobre 7 días; rechazo verde hasta 5%, ámbar hasta 20% y rojo por encima. Use el selector para alternar entre laboratorios y distribuidoras.',
  },
  {
    corto: 'Rechazos',
    titulo: 'Rechazos',
    route: '/reportes/rechazos',
    pregunta: '¿qué se rechaza, por qué motivo y quién concentra los rechazos?',
    filtros: 'Desde, Hasta, Laboratorio y filas de detalle.',
    tarjetas: [
      {
        nombre: 'Tasa de rechazo',
        significado:
          'rechazadas sobre recibidas en la misma ventana, no sobre el histórico.',
      },
      {
        nombre: 'Valor rechazado (CIF)',
        significado: 'monto que hubo que reprocesar.',
      },
      {
        nombre: 'Laboratorios afectados',
        significado: 'cuántos tuvieron al menos un rechazo.',
      },
      {
        nombre: 'Sin motivo registrado',
        significado: 'casos donde no se puede analizar la causa.',
      },
    ],
    aviso:
      'la tabla por laboratorio marca en rojo a quienes superan el 20% de rechazo. Ahí es donde suele estar la causa raíz accionable, no en el total global.',
  },
]

const ESTADO_DATOS = [
  {
    campo: 'Motivo de rechazo',
    ok: true,
    implica: 'Los rechazos son analizables: el motivo sí se registra.',
  },
  {
    campo: 'Marca de liquidado',
    ok: false,
    implica: 'Histórico sin depurar: infla la cartera pendiente.',
  },
  {
    campo: 'Estado del lote',
    ok: false,
    implica: 'No coincide con la fecha de vencimiento. Se usa la fecha.',
  },
  {
    campo: 'Certificado de lote',
    ok: false,
    implica: 'Casi todos en cero: confirmar si el campo sigue en uso.',
  },
  {
    campo: 'Fecha de entrega',
    ok: false,
    implica: 'Casi no se captura: el ciclo llega sólo hasta salida de F&D.',
  },
]

export default {
  data() {
    return {
      reportes: REPORTES,
      estadoDatos: ESTADO_DATOS,
    }
  },
}
</script>

<style scoped>
.guide-page {
  padding-bottom: 32px;
}

/* === Hero === */
.list-hero {
  margin-bottom: 8px;
}
.list-crumb {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0;
}
.list-title {
  font-size: 30px;
  font-weight: 700;
  color: #0f172a;
  margin: 4px 0 6px;
}
.list-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 760px;
}

/* === Card === */
.guide-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
  margin-bottom: 16px;
  padding: 18px 20px;
}

/* === Paso === */
.guide-step {
  display: flex;
  gap: 14px;
}
.guide-step__num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #254f87;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.guide-step__title {
  margin: 4px 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}
.guide-step__text {
  margin: 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}
.guide-section__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}
.guide-label {
  margin: 12px 0 4px;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
}

.guide-fields {
  margin: 0;
  padding-left: 18px;
}
.guide-fields li {
  font-size: 13.5px;
  color: #475569;
  line-height: 1.7;
}

/* Aviso de lectura: terracota del sistema, para que se distinga de un tip. */
.guide-warn {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #fdf1ee;
  border: 1px solid #f2d5cd;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
}
.guide-warn__icon {
  color: #bd614d !important;
  margin-top: 1px;
}

.guide-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  background: #254f87;
  color: #ffffff !important;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.18s ease;
}
.guide-cta:hover {
  background: #1e4272;
  transform: translateY(-1px);
}
.guide-cta__icon {
  color: #ffffff !important;
}

/* === Panel lateral === */
.guide-aside__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.guide-aside__icon {
  color: #254f87 !important;
}
.guide-aside__list {
  margin: 0;
  padding-left: 18px;
}
.guide-aside__list li {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 4px;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }
}
</style>
